import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { FiGrid, FiArrowLeft, FiLogOut, FiCalendar } from 'react-icons/fi';
import { departmentAPI, projectAPI, userAPI } from '../utils/api';
import { getUser, logout as logoutAuth, saveUser } from '../utils/auth';
import { authAPI } from '../utils/api';
import { normalizeAlphaNumLower } from '../utils/string';
import {
  FINANCE_DASHBOARD_PROJECTS as FINANCE_VIRTUAL,
  OM_DASHBOARD_PROJECTS as OM_VIRTUAL,
  HR_DASHBOARD_PROJECTS as HR_VIRTUAL,
  FINANCE_DUPLICATE_NAME_SET,
  OM_DUPLICATE_NAME_SET,
  HR_DUPLICATE_NAME_SET
} from '../utils/dashboardProjects';
import { isOmDepartmentKey, isHrDepartmentKey } from '../utils/departmentKeys';
import './ExcoLandingPage.css';

const normalizeDepartmentKey = (value) =>
  normalizeAlphaNumLower(value);

const normalizeProjectName = (value) =>
  normalizeAlphaNumLower(value);

const getDepartmentPriority = (name) => {
  const key = normalizeDepartmentKey(name);
  if (key === 'finance') return 0;
  if (isOmDepartmentKey(key)) return 1;
  return 2;
};

const sortDepartments = (depts) =>
  [...(Array.isArray(depts) ? depts : [])].sort((a, b) => {
    const diff = getDepartmentPriority(a?.name) - getDepartmentPriority(b?.name);
    if (diff !== 0) return diff;
    return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' });
  });

const DEPT_ICONS = {
  finance: '💰',
  hr: '👥',
  it: '💻',
  operations: '⚙️',
};

const getDeptIcon = (name) => {
  const key = normalizeDepartmentKey(name);
  if (isOmDepartmentKey(key)) return '🔧';
  return DEPT_ICONS[key] || '📊';
};

const isHrDepartment = (department) => isHrDepartmentKey(normalizeDepartmentKey(department?.name));

const getSubDashboards = (department, allProjects) => {
  const deptId = Number(department?.id);
  const deptKey = normalizeDepartmentKey(department?.name);
  const deptProjects = (Array.isArray(allProjects) ? allProjects : [])
    .filter((p) => Number(p.departmentId) === deptId);

  if (deptKey === 'finance') {
    const real = deptProjects.filter((p) => !FINANCE_DUPLICATE_NAME_SET.has(normalizeProjectName(p?.name)));
    return [...FINANCE_VIRTUAL.map((p) => ({ ...p, departmentId: deptId })), ...real];
  }

  if (isOmDepartmentKey(deptKey)) {
    const real = deptProjects.filter((p) => !OM_DUPLICATE_NAME_SET.has(normalizeProjectName(p?.name)));
    return [...OM_VIRTUAL.map((p) => ({ ...p, departmentId: deptId })), ...real];
  }

  if (isHrDepartment(department)) {
    const real = deptProjects.filter((p) => !HR_DUPLICATE_NAME_SET.has(normalizeProjectName(p?.name)));
    return [...HR_VIRTUAL.map((p) => ({ ...p, departmentId: deptId })), ...real];
  }

  return deptProjects;
};

const ExcoLandingPage = ({ setIsLoggedIn, setUser }) => {
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [user, setCurrentUser] = useState(getUser());

  useEffect(() => {
    const timer = setInterval(() => setCurrentDate(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const syncCurrentUserAccess = async () => {
      const currentUser = getUser();
      const userId = Number(currentUser?.id);
      if (!Number.isInteger(userId) || userId <= 0) return;

      try {
        const response = await userAPI.getUserById(userId);
        if (!response?.success || !response?.data) return;

        const mergedUser = {
          ...currentUser,
          ...response.data,
          departmentIds: Array.isArray(response.data.departmentIds) ? response.data.departmentIds : (currentUser?.departmentIds || []),
          excoDashboardAccess: response.data.excoDashboardAccess || currentUser?.excoDashboardAccess || {}
        };

        saveUser(mergedUser);
        setCurrentUser(mergedUser);
      } catch (error) {
        if (error?.statusCode === 403) return; // user doesn't have users:read permission
        console.error('Error syncing EXCO user session:', error);
      }
    };

    syncCurrentUserAccess();

    globalThis.addEventListener('focus', syncCurrentUserAccess);
    return () => globalThis.removeEventListener('focus', syncCurrentUserAccess);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deptRes, projRes] = await Promise.all([
          departmentAPI.getAllDepartments(),
          projectAPI.getAllProjects(),
        ]);
        setDepartments(Array.isArray(deptRes?.data) ? deptRes.data : []);
        setProjects(Array.isArray(projRes?.data) ? projRes.data : []);
      } catch (e) {
        console.error('ExcoLanding fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await authAPI.logout();
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      logoutAuth();
      setIsLoggedIn(false);
      if (setUser) setUser(null);
    }
  };

  const handleDeptClick = (dept) => setSelectedDept(dept);

  const handleSubDashboardClick = useCallback((dept, project) => {
    localStorage.setItem('selectedDept', JSON.stringify(dept));
    localStorage.setItem('selectedProject', JSON.stringify(project));
    globalThis.dispatchEvent(new Event('departmentChanged'));
    globalThis.dispatchEvent(new Event('projectChanged'));
    navigate('/dashboard');
  }, [navigate]);

  const sortedDepartments = sortDepartments(departments);
  const subDashboards = selectedDept ? getSubDashboards(selectedDept, projects) : [];
  const hrMainDashboard = isHrDepartment(selectedDept)
    ? subDashboards.find((project) => String(project?.id || '').trim().toLowerCase() === 'hr-main-dashboard') || null
    : null;
  const hrSubDashboards = isHrDepartment(selectedDept)
    ? subDashboards.filter((project) => String(project?.id || '').trim().toLowerCase() !== 'hr-main-dashboard')
    : [];

  const formattedDate = currentDate.toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="exco-landing">
      <nav className="exco-navbar">
        <div className="exco-navbar-inner">
          <div className="exco-navbar-brand">
            <img
              src="https://inveniatech.com/wp-content/uploads/2024/08/Invenia-logo.png"
              alt="Invenia"
              className="exco-navbar-logo"
            />
            <span className="exco-navbar-title">DISHA</span>
          </div>
          <div className="exco-navbar-right">
            <span className="exco-navbar-date">
              <FiCalendar size={13} className="me-1" />
              {formattedDate}
            </span>
            <button
              className="exco-navbar-logout"
              onClick={handleLogout}
              disabled={loggingOut}
              title="Logout"
            >
              {loggingOut ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FiLogOut size={15} className="me-1" />
                  Logout
                </>
              )}
            </button>
          </div>
        </div>
      </nav>

      <div className="exco-body">
        {loading ? (
          <div className="exco-loading">
            <Spinner animation="border" />
          </div>
        ) : !selectedDept ? (
          <>
            {user?.firstName && (
              <p className="exco-greeting">Welcome, {user.firstName}</p>
            )}
            <h2 className="exco-section-title">
              <FiGrid className="me-2" />
              Select a Department
            </h2>
            <div className="exco-tiles">
              {sortedDepartments.map((dept) => (
                <button key={dept.id} className="exco-tile" onClick={() => handleDeptClick(dept)}>
                  <span className="exco-tile-icon">{getDeptIcon(dept.name)}</span>
                  <span className="exco-tile-name">{dept.name}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="exco-back-row">
              <button className="exco-back-btn" onClick={() => setSelectedDept(null)}>
                <FiArrowLeft size={16} className="me-1" />
                Back to Departments
              </button>
              <h2 className="exco-section-title mb-0">
                {getDeptIcon(selectedDept.name)} {selectedDept.name} - Select Dashboard
              </h2>
            </div>

            {isHrDepartment(selectedDept) ? (
              <div className="exco-hr-layout">
                {hrMainDashboard && (
                  <div className="exco-hr-main-wrap">
                    <p className="exco-panel-label">Main Dashboard</p>
                    <button
                      className="exco-tile exco-tile-primary"
                      onClick={() => handleSubDashboardClick(selectedDept, hrMainDashboard)}
                    >
                      <span className="exco-tile-icon">📈</span>
                      <span className="exco-tile-name">{hrMainDashboard.name}</span>
                    </button>
                  </div>
                )}

                {hrSubDashboards.length > 0 && (
                  <div className="exco-hr-sub-wrap">
                    <p className="exco-panel-label">Sub Dashboards</p>
                    <div className="exco-tiles exco-sub-tiles">
                      {hrSubDashboards.map((project) => (
                        <button
                          key={project.id}
                          className="exco-tile exco-sub-tile"
                          onClick={() => handleSubDashboardClick(selectedDept, project)}
                        >
                          <span className="exco-tile-icon">📈</span>
                          <span className="exco-tile-name">{project.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!hrMainDashboard && hrSubDashboards.length === 0 && (
                  <p className="exco-empty">No dashboards available for this department.</p>
                )}
              </div>
            ) : (
              <div className="exco-tiles">
                {subDashboards.length === 0 ? (
                  <p className="exco-empty">No dashboards available for this department.</p>
                ) : (
                  subDashboards.map((project) => (
                    <button
                      key={project.id}
                      className="exco-tile"
                      onClick={() => handleSubDashboardClick(selectedDept, project)}
                    >
                      <span className="exco-tile-icon">📈</span>
                      <span className="exco-tile-name">{project.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExcoLandingPage;
